import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, resolveImage } from "@/lib/api";
import { useBrands } from "@/components/Yachts";
import { BrandLogo } from "@/components/Brands";
import YachtCard from "@/components/YachtCard";
import BrandTheme from "@/components/BrandTheme";


export default function BrandPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState("All");
  const { data: brands = [] } = useBrands();
  const { data: brand, isLoading, isError } = useQuery({
    queryKey: ["brand", slug],
    queryFn: async () => (await api.get(`/brands/${slug}`)).data,
  });

  if (isLoading) {
    return <div className="px-6 pt-40 text-sm text-mist lg:px-12" data-testid="brand-page-loading">Loading…</div>;
  }
  if (isError || !brand) {
    return (
      <div className="px-6 pb-24 pt-40 lg:px-12" data-testid="brand-page-not-found">
        <p className="font-serif text-3xl text-navy-ink">This marque could not be found.</p>
        <Link to="/yachts" className="mt-6 inline-block text-[11px] uppercase tracking-[0.3em] text-gold">Back to all marques</Link>
      </div>
    );
  }

  const categories = ["All", ...new Set(brand.yachts.map((y) => y.category).filter(Boolean))];
  const shown = category === "All" ? brand.yachts : brand.yachts.filter((y) => y.category === category);
  const others = brands.filter((b) => b.slug !== slug);

  return (
    <BrandTheme theme={brand.theme} data-testid="brand-page">
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden" data-testid="brand-hero">
        <img src={resolveImage(brand.hero_image)} alt={brand.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-navy to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-16 lg:px-12">
          <div className="mx-auto max-w-7xl">
            <Link to="/yachts" className="mb-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-white/80 transition-colors hover:text-gold-pale" data-testid="brand-back-link">
              <ArrowLeft size={12} /> All marques
            </Link>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-4 text-[10px] uppercase tracking-[0.45em] text-gold-pale sm:text-xs">
              {brand.origin}
            </motion.p>
            {brand.logo_light ? (
              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="py-2" data-testid="brand-heading">
                <img src={resolveImage(brand.logo_light)} alt={brand.name} className="h-auto w-[min(70vw,560px)]" data-testid="brand-hero-logo" />
                <span className="sr-only">{brand.name}</span>
              </motion.h1>
            ) : (
              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="brand-heading font-serif text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl" data-testid="brand-heading">
                {brand.name}
              </motion.h1>
            )}
          </div>
        </div>
      </section>

      <section className="brand-surface px-6 py-20 lg:px-12 lg:py-28" data-testid="brand-intro">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            {!brand.logo_light && (
              <div className="flex h-20 items-center overflow-hidden">
                <BrandLogo brand={brand} className="max-h-14" />
              </div>
            )}
            <p className="brand-heading brand-ink mt-6 font-serif text-2xl italic text-gold-light">{brand.tagline}</p>
          </div>
          <p className="brand-body brand-ink max-w-2xl text-base font-light leading-relaxed text-mist lg:col-span-8" data-testid="brand-description">
            {brand.description}
          </p>
        </div>
      </section>

      <section className="bg-navy px-6 pb-24 lg:px-12 lg:pb-36" data-testid="brand-models">
        <div className="mx-auto max-w-7xl">
          <div className="mb-14 flex flex-col justify-between gap-8 border-t border-navy-ink/10 pt-14 lg:flex-row lg:items-end">
            <h2 className="brand-heading brand-ink font-serif text-4xl font-light tracking-tight text-navy-ink sm:text-5xl" data-testid="brand-models-heading">
              The range <span className="text-mist">— {brand.yachts.length} {brand.yachts.length === 1 ? "model" : "models"}</span>
            </h2>
            {categories.length > 2 && (
              <div className="flex flex-wrap gap-2" data-testid="brand-category-filter">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`brand-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`rounded-full border px-5 py-2 text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
                      category === c ? "border-gold bg-gold text-navy" : "border-navy-ink/15 text-mist hover:border-gold hover:text-gold"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {shown.length === 0 && <p className="text-sm text-mist" data-testid="brand-models-empty">No models listed yet. Please enquire for the latest availability.</p>}

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {shown.map((y, i) => (
              <YachtCard key={y.id} yacht={y} index={i} compact brandLabel={`${brand.name} · ${brand.origin}`} />
            ))}
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="border-t border-navy-ink/5 bg-navy-light/60 px-6 py-20 lg:px-12" data-testid="brand-others">
          <div className="mx-auto max-w-7xl">
            <p className="mb-8 text-[10px] uppercase tracking-[0.45em] text-gold">Explore other marques</p>
            <div className="grid gap-4 sm:grid-cols-3">
              {others.map((b) => (
                <Link key={b.slug} to={`/yachts/${b.slug}`} className="group flex items-center justify-between border border-navy-ink/10 bg-navy p-6 transition-colors duration-300 hover:border-gold/40" data-testid={`brand-other-${b.slug}`}>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-mist">{b.origin}</p>
                    <p className="mt-1 font-serif text-xl text-navy-ink">{b.name}</p>
                  </div>
                  <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </BrandTheme>
  );
}
